/**
 * Fallback-landtarieven zonder databaseafhankelijkheid, zodat ook clientcomponenten
 * (zoals de productconfigurator) het minimale verzendtarief kunnen tonen.
 * rates.ts blijft de ingang voor server-code en exporteert dit opnieuw.
 */

export type CountryRates = {
  countryCode: string;
  vatRate: number;
  shippingCost: number;
  freeShippingFrom: number;
  currency: string;
};

export const FALLBACK_COUNTRY_RATES: Record<string, CountryRates> = {
  NL: { countryCode: 'NL', vatRate: 21, shippingCost: 6.95, freeShippingFrom: 150, currency: 'EUR' },
  BE: { countryCode: 'BE', vatRate: 21, shippingCost: 7.95, freeShippingFrom: 250, currency: 'EUR' },
  DE: { countryCode: 'DE', vatRate: 19, shippingCost: 8.95, freeShippingFrom: 500, currency: 'EUR' },
  FR: { countryCode: 'FR', vatRate: 20, shippingCost: 9.95, freeShippingFrom: 500, currency: 'EUR' },
  DK: { countryCode: 'DK', vatRate: 25, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  ES: { countryCode: 'ES', vatRate: 21, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  IT: { countryCode: 'IT', vatRate: 22, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  LU: { countryCode: 'LU', vatRate: 17, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  PL: { countryCode: 'PL', vatRate: 23, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  AT: { countryCode: 'AT', vatRate: 20, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  CZ: { countryCode: 'CZ', vatRate: 21, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  EE: { countryCode: 'EE', vatRate: 24, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  HR: { countryCode: 'HR', vatRate: 25, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  HU: { countryCode: 'HU', vatRate: 27, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  LT: { countryCode: 'LT', vatRate: 21, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  LV: { countryCode: 'LV', vatRate: 21, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  SE: { countryCode: 'SE', vatRate: 25, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  SI: { countryCode: 'SI', vatRate: 22, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  SK: { countryCode: 'SK', vatRate: 23, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  IE: { countryCode: 'IE', vatRate: 23, shippingCost: 14.50, freeShippingFrom: 500, currency: 'EUR' },
  // Zwitserland is geen EU-land: levering is DDU (delivered duty unpaid).
  // De prijs is dus exportprijs zonder btw; Zwitserse invoer-btw en eventuele
  // rechten zijn voor de klant bij invoer, niet inbegrepen in dit bedrag.
  CH: { countryCode: 'CH', vatRate: 0, shippingCost: 29.95, freeShippingFrom: 500, currency: 'EUR' },
};

export function fallbackRatesFor(countryCode?: string): CountryRates {
  const normalized = String(countryCode || 'NL').toUpperCase();
  return FALLBACK_COUNTRY_RATES[normalized] || FALLBACK_COUNTRY_RATES.NL;
}

/**
 * Landen waarvoor de shop btw- en verzendtarieven heeft. Alleen naar deze
 * landen mag een bestelling, offerte of wederverkopersorder worden
 * aangemaakt. Een ander land wordt geweigerd en NOOIT stil als Nederland
 * doorgerekend: dat gaf het Nederlandse btw-tarief en NL-verzendkosten aan een
 * pakket dat naar bijvoorbeeld Portugal ging.
 */
export const SUPPORTED_ORDER_COUNTRY_CODES: ReadonlySet<string> = new Set(Object.keys(FALLBACK_COUNTRY_RATES));

export function isSupportedOrderCountry(countryCode: unknown): boolean {
  return SUPPORTED_ORDER_COUNTRY_CODES.has(String(countryCode || '').trim().toUpperCase());
}

export class UnsupportedCountryError extends Error {
  readonly code = 'UNSUPPORTED_COUNTRY' as const;
  readonly countryCode: string;

  constructor(countryCode: string) {
    super(unsupportedCountryMessage(countryCode, 'nl'));
    this.name = 'UnsupportedCountryError';
    this.countryCode = countryCode;
  }
}

export function isUnsupportedCountryError(error: unknown): error is UnsupportedCountryError {
  return error instanceof UnsupportedCountryError
    || (typeof error === 'object' && error !== null && (error as { code?: unknown }).code === 'UNSUPPORTED_COUNTRY');
}

function countryDisplayName(countryCode: string, lang: 'nl' | 'en' | 'de' | 'fr'): string {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return code || '?';
  try {
    const name = new Intl.DisplayNames([lang], { type: 'region' }).of(code);
    return name && name !== code ? name : code;
  } catch {
    return code;
  }
}

/** Klantgerichte weigering in nl/en/de/fr; overige locales vallen terug op Engels. */
export function unsupportedCountryMessage(countryCode: string, locale?: string | null): string {
  const raw = String(locale || 'nl').toLowerCase().slice(0, 2);
  const lang = (raw === 'nl' || raw === 'de' || raw === 'fr' || raw === 'en') ? raw : 'en';
  const name = countryDisplayName(countryCode, lang);
  switch (lang) {
    case 'nl': return `We verzenden (nog) niet naar ${name}. Kies een ander bezorgadres of neem contact met ons op.`;
    case 'de': return `Wir liefern (noch) nicht nach ${name}. Bitte wählen Sie eine andere Lieferadresse oder kontaktieren Sie uns.`;
    case 'fr': return `Nous ne livrons pas (encore) dans ce pays : ${name}. Choisissez une autre adresse de livraison ou contactez-nous.`;
    default: return `We do not ship to ${name} (yet). Please choose another delivery address or contact us.`;
  }
}
